import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  Heart, 
  Star, 
  ChevronLeft, 
  ChevronRight, 
  Check,
  Share2,
  Minus,
  Plus,
  Leaf,
  Droplets,
  Sparkles,
  Award
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { getProductById, getRelatedProducts, Product } from '@/data/products';
import { DynamicLogo } from '@/components/DynamicLogo';
import { SEO } from '@/components/SEO';
import { useShop } from '@/context/ShopContext';

export function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addToCart, addToWishlist, isInWishlist } = useShop();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'ingredients' | 'howToUse'>('description');

  useEffect(() => {
    if (productId) {
      const foundProduct = getProductById(productId);
      if (foundProduct) {
        setProduct(foundProduct);
        setRelatedProducts(getRelatedProducts(foundProduct));
        setSelectedImage(0);
        setQuantity(1);
        window.scrollTo(0, 0);
      } else {
        navigate('/shop');
      }
    }
  }, [productId, navigate]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <DynamicLogo size="lg" animate />
          <p className="mt-4 text-[#6B7280]">Gathering from the garden…</p>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    setIsAddingToCart(true);
    addToCart(product, quantity);
    setTimeout(() => setIsAddingToCart(false), 1500);
  };

  const categoryColors: Record<string, { main: string; accent: string }> = {
    candles: { main: '#42C5E7', accent: '#E07A5F' },
    tea: { main: '#BDD630', accent: '#567D38' },
    'body-care': { main: '#F4C95D', accent: '#E07A5F' },
    skincare: { main: '#E07A5F', accent: '#F4C95D' },
  };

  const colors = categoryColors[product.category] || categoryColors['body-care'];

  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": `https://marplecompany.co.za/products/${product.id}-1.jpg`,
    "url": `https://marplecompany.co.za/product/${product.id}`,
    "brand": {
      "@type": "Brand",
      "name": "Marple & Co",
      "@id": "https://marplecompany.co.za/#organization"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Marple & Co",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Modderfontein",
        "addressRegion": "Edenvale, Gauteng",
        "addressCountry": "ZA"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": "ZAR",
      "availability": "https://schema.org/InStock",
      "url": `https://marplecompany.co.za/product/${product.id}`,
      "seller": { "@id": "https://marplecompany.co.za/#organization" },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "ZAR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "ZA" },
        "doesNotShip": false,
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "businessDays": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"] },
          "cutoffTime": "12:00:00",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "DAY" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 5, "unitCode": "DAY" }
        }
      }
    },
    "additionalProperty": product.keyIngredients?.map(ing => ({
      "@type": "PropertyValue",
      "name": "Key Ingredient",
      "value": ing
    }))
  } : undefined;

  return (
    <div className="min-h-screen bg-white pt-20">
      {product && (
        <SEO
          title={`${product.name} | Marple & Co`}
          description={product.description}
          path={`/product/${product.id}`}
          image={`https://marplecompany.co.za/products/${product.id}-1.jpg`}
          type="product"
          schema={productSchema}
        />
      )}
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="container-marple py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-[#1A2E12] transition-colors">Home</Link>
            <span className="text-gray-300">/</span>
            <Link to="/shop" className="hover:text-[#1A2E12] transition-colors">Shop</Link>
            <span className="text-gray-300">/</span>
            <span className="text-[#1A2E12] font-medium truncate max-w-[200px]">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="py-8 lg:py-12">
        <div className="container-marple">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div 
                className="relative aspect-square rounded-xl overflow-hidden"
                style={{ backgroundColor: `${colors.main}15` }}
              >
                <div className="absolute inset-0 flex items-center justify-center p-8 lg:p-12">
                  <div 
                    className="relative w-full max-w-[350px] border-4 p-6 lg:p-8 bg-white rounded-xl shadow-lg"
                    style={{ borderColor: colors.main }}
                  >
                    <DynamicLogo size="sm" />
                    <h3 className="font-display font-bold text-xl lg:text-2xl text-[#1A2E12] uppercase mt-4">
                      {product.name}
                    </h3>
                    <p className="font-elegant italic text-[#567D38] text-base lg:text-lg">
                      {product.subtitle}
                    </p>
                    {product.scent && (
                      <span 
                        className="label text-xs mt-3 inline-block"
                        style={{ color: colors.accent }}
                      >
                        • {product.scent} •
                      </span>
                    )}
                  </div>
                </div>

                {/* Navigation Arrows */}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImage(prev => prev === 0 ? product.images.length - 1 : prev - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSelectedImage(prev => prev === product.images.length - 1 ? 0 : prev + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {product.isNew && (
                    <span className="px-3 py-1 bg-[#42C5E7] text-white text-xs font-bold uppercase tracking-wide rounded-full">
                      New
                    </span>
                  )}
                  {product.isBestseller && (
                    <span className="px-3 py-1 bg-[#F4C95D] text-[#1A2E12] text-xs font-bold uppercase tracking-wide rounded-full">
                      Bestseller
                    </span>
                  )}
                </div>

                {/* Wishlist */}
                <button
                  onClick={() => addToWishlist(product.id)}
                  className={cn(
                    "absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors shadow-md",
                    isInWishlist(product.id) 
                      ? "bg-[#E07A5F] text-white" 
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  )}
                >
                  <Heart className={cn("w-5 h-5", isInWishlist(product.id) && "fill-current")} />
                </button>
              </div>

              {/* Thumbnail Gallery */}
              {product.images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {product.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={cn(
                        "flex-shrink-0 w-16 h-16 border-2 rounded-lg transition-colors overflow-hidden",
                        selectedImage === index 
                          ? "border-[#1A2E12]" 
                          : "border-gray-200 hover:border-gray-400"
                      )}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: `${colors.main}15` }}
                      >
                        <DynamicLogo size="sm" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-5">
              {/* Category & Rating */}
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  {product.subcategory && (
                    <span 
                      className="label px-3 py-1 rounded-full"
                      style={{ color: colors.accent, backgroundColor: `${colors.accent}15` }}
                    >
                      {product.subcategory}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "w-4 h-4",
                          i < Math.floor(product.rating)
                            ? "text-[#F4C95D] fill-[#F4C95D]"
                            : "text-gray-300"
                        )}
                      />
                    ))}
                    <span className="text-sm text-gray-500 ml-1">
                      ({product.reviews})
                    </span>
                  </div>
                </div>

                <h1 className="font-display font-bold text-2xl lg:text-4xl text-[#1A2E12] uppercase tracking-tight">
                  {product.name}
                </h1>
                <p className="font-elegant italic text-lg text-[#567D38]">
                  {product.subtitle}
                </p>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="font-display font-bold text-3xl lg:text-4xl text-[#1A2E12]">
                  R{product.price.toFixed(2)}
                </span>
                {product.originalPrice && (
                  <span className="text-lg text-gray-400 line-through">
                    R{product.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Short Description */}
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>

              {/* Size */}
              <div className="flex items-center gap-4">
                <span className="label text-gray-500">SIZE</span>
                <span className="px-4 py-2 bg-[#1A2E12] text-white font-medium rounded-lg">
                  {product.size}
                </span>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex gap-3 flex-wrap">
                <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-12 text-center font-display font-bold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAddingToCart || !product.inStock}
                  className={cn(
                    "flex-1 btn btn-primary min-w-[200px]",
                    isAddingToCart && "bg-green-600",
                    !product.inStock && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {isAddingToCart ? (
                    <>
                      <Check className="w-5 h-5" />
                      Added!
                    </>
                  ) : !product.inStock ? (
                    "Out of Stock"
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5" />
                      Add to Cart — R{(product.price * quantity).toFixed(2)}
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate('/regimen')}
                  className="btn btn-secondary min-w-[180px]"
                >
                  Add to regimen
                </button>
              </div>

              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-leroy-chat'))}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-[#567D38] bg-[#BDD630]/15 hover:bg-[#BDD630]/25 transition-colors w-fit"
              >
                💬 Ask Leroy about this product
              </button>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-3 py-4 border-y border-gray-100">
                {[
                  { icon: Leaf, text: 'All Natural' },
                  { icon: Droplets, text: 'Handmade' },
                  { icon: Sparkles, text: 'Cruelty Free' },
                  { icon: Award, text: 'Premium Quality' },
                ].map((benefit) => (
                  <div key={benefit.text} className="flex items-center gap-2">
                    <benefit.icon className="w-5 h-5 text-[#567D38]" />
                    <span className="text-sm font-medium text-gray-700">{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-2.5 h-2.5 rounded-full",
                  product.stockCount > 10 ? "bg-green-500" : 
                  product.stockCount > 0 ? "bg-yellow-500" : "bg-red-500"
                )} />
                <span className="text-sm text-gray-600">
                  {product.stockCount > 10 ? 'In Stock' : 
                   product.stockCount > 0 ? `Only ${product.stockCount} left` : 'Out of Stock'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details Tabs - Fixed spacing */}
      <section className="py-12 bg-gray-50">
        <div className="container-marple">
          {/* Tab Navigation */}
          <div className="flex flex-wrap border-b border-gray-200 mb-6">
            {[
              { id: 'description', label: 'Description' },
              { id: 'ingredients', label: 'Ingredients' },
              { id: 'howToUse', label: 'How to use' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "px-4 lg:px-6 py-3 font-display text-sm font-semibold uppercase tracking-wide transition-colors whitespace-nowrap",
                  activeTab === tab.id
                    ? "text-[#1A2E12] border-b-2 border-[#1A2E12]"
                    : "text-gray-500 hover:text-[#1A2E12]"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
            {activeTab === 'description' && (
              <div className="max-w-3xl space-y-6">
                <p className="text-gray-700 leading-relaxed text-base lg:text-lg">
                  {product.longDescription}
                </p>
                <div className="space-y-3">
                  <h3 className="font-display font-bold text-lg text-[#1A2E12]">Benefits</h3>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {product.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#BDD630] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'ingredients' && (
              <div className="max-w-3xl space-y-6">
                <p className="text-gray-600">
                  Our products are crafted with the finest natural ingredients sourced from across Africa.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {product.keyIngredients.map((ingredient, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-[#1A2E12] transition-colors"
                    >
                      <span className="font-display font-semibold text-[#1A2E12]">{ingredient}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'howToUse' && (
              <div className="max-w-3xl">
                <p className="text-gray-700 leading-relaxed text-base lg:text-lg">
                  {product.howToUse}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="container-marple">
            <div className="text-center mb-8">
              <span className="font-display uppercase tracking-[.06em] text-xs font-semibold text-[#567D38]">
                Pairs well with
              </span>
              <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#1A2E12] mt-2">
                Complete the ritual
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group card"
                >
                  <div 
                    className="aspect-square p-4 flex items-center justify-center"
                    style={{ backgroundColor: `${categoryColors[relatedProduct.category]?.main || '#F4C95D'}15` }}
                  >
                    <div className="border-2 p-3 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow" style={{ borderColor: categoryColors[relatedProduct.category]?.main || '#F4C95D' }}>
                      <DynamicLogo size="sm" />
                      <p className="font-display font-bold text-xs text-[#1A2E12] uppercase mt-2 text-center">
                        {relatedProduct.name}
                      </p>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-[#1A2E12] group-hover:text-[#567D38] transition-colors text-sm lg:text-base truncate">
                      {relatedProduct.name}
                    </h3>
                    <p className="font-display font-bold text-[#1A2E12] mt-1">
                      R{relatedProduct.price.toFixed(2)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ProductDetail;
